<template>
  <div class="exam-question-card w-100">
    <!-- TOP ROW -->
    <div class="top-row w-100" @click="toggleAccordion">
      <div class="left-row">
        <!-- COUNTER -->
        <div class="counter">
          <div class="count">{{ counter }}</div>
        </div>

        <div class="question-info">
          <div class="question-text" v-html="question.question"></div>

          <!-- QUESTION IMAGE -->
          <div
            class="question-image my-3 position-relative w-auto"
            v-if="question.image"
          >
            <img :src="question.image" alt="Question Image" class="pointer" />
          </div>

          <div class="question-detail">
            <span class="mr-1" :title="question.topic_details.topic">{{
              shortenedTopic
            }}</span>
            • <span class="mx-1">{{ question.score }} Points</span> •
            <span
              class="ml-1 font-weight-600 text-capitalize"
              :class="getDifficultyColor"
              >{{ question.difficulty }}</span
            >
          </div>
        </div>
      </div>

      <div class="right-row pointer">
        <div
          class="icon icon-caret-down brand_inverse"
          :class="{ 'rotate-180': show_accordion }"
        ></div>
      </div>
    </div>

    <!-- BOTTOM ROW -->
    <div class="bottom-row" v-if="show_accordion">
      <div class="spacer"></div>

      <!-- OPTION BLOCK -->
      <div class="option-block">
        <!-- TOP OPTION -->
        <div class="top-option">
          <div class="text">Answer Options</div>
        </div>

        <!-- MID OPTION -->
        <div class="mid-option">
          <exam-option
            v-for="(option, index) in options"
            :key="index"
            :counter="index + 1"
            :option="option"
            :answer="question.answer"
          />
        </div>

        <!-- BOTTOM OPTION -->
        <div class="bottom-option">
          <div
            class="exam-btn delete-btn"
            @click="$emit('remove-question', question.id)"
          >
            <div class="icon icon-thrash"></div>
            <div class="text">Remove</div>
          </div>

          <div class="exam-btn report-btn" v-if="false">
            <div class="icon icon-flag"></div>
            <div class="text">Report</div>
          </div>
        </div>
      </div>
    </div>

    <script
      type="application/javascript"
      defer
      async="true"
      src="https://www.wiris.net/demo/plugins/app/WIRISplugins.js?viewer=image"
    ></script>
  </div>
</template>

<script>
import ExamOption from "@/components/schoolComps/dashboard/home/ExamOption";

export default {
  name: "ExamQuestionCard",

  components: {
    ExamOption,
  },

  props: {
    counter: Number,
    question: Object,
  },

  computed: {
    shortenedTopic() {
      if (this.question.topic_details.topic.length > 40)
        return `${this.question.topic_details.topic.slice(0, 40)}...`;
      else return this.question.topic_details.topic;
    },

    getDifficultyColor() {
      if (this.question.difficulty === "easy") return "brand_navy";
      else if (this.question.difficulty === "medium") return "brand_accent";
      else if (this.question.difficulty === "hard") return "brand_tonic";
      else return false;
    },

    getQuestionAnswer() {
      // MULTIPLE QUESTION TYPE
      if (this.question?.type === "multiple") {
        let answer = this.convertValueToNum(this.question?.answer);
        return this.stripBreakTags(this.options[answer]);
      }
      // BOOL QUESTION TYPE
      else if (this.question?.type === "bool") {
        if (this.question?.answer === "1") return "True";
        else return "False";
      }
      //   SHORT ANSWER
      else if (this.question?.type === "short") {
        return this.showShortAnswer();
      } else return false;
    },
  },

  watch: {
    question: {
      handler(value) {
        if (Object.keys(value).length) {
          this.formatQuestion();
          this.loadQuestionOptionsIfMultiple();
        }
      },
      immediate: true,
    },
  },

  data: () => ({
    show_accordion: false,
    options: [],
  }),

  methods: {
    toggleAccordion() {
      this.show_accordion = !this.show_accordion;
    },

    // FORMAT QUESTION
    formatQuestion() {
      this.$nextTick(() => {
        let small_screen = window.window.matchMedia("(max-width: 568px)");
        let extra_small_screen = window.window.matchMedia("(max-width: 450px)");

        document.querySelectorAll(".question-info").forEach((element) => {
          element.children.forEach((elm) => {
            // REMOVE BREAK TAGS
            elm.querySelectorAll("br").forEach((ele) => ele.remove());

            elm.querySelectorAll("figure").forEach((figure) => {
              figure.querySelector("img").style.minWidth = "25%";
              figure.querySelector("img").style.width = "auto";
              figure.querySelector("img").style.maxWidth = "50%";
              figure.querySelector("img").style.height = "auto";
              figure.querySelector("img").style.maxHeight = "260px";

              // WATCH FOR CHANGES IN SCREEN SIZES
              if (small_screen.matches)
                figure.querySelector("img").style.maxWidth = "60%";

              if (extra_small_screen.matches)
                figure.querySelector("img").style.maxHeight = "200px";
            });
          });
        });
      });
    },

    stripBreakTags(value) {
      return value?.replace("<br />", "");
    },

    // LOAD QUESTION OPTION IF IT IS MULTIPLE TYPE
    loadQuestionOptionsIfMultiple() {
      if (this.question?.type === "multiple") {
        this.options = [
          this.question?.option_a,
          this.question?.option_b,
          this.question?.option_c,
          this.question?.option_d,
        ];
      }
    },

    convertValueToNum(value) {
      if (value.toLowerCase() === "a") return 0;
      else if (value.toLowerCase() === "b") return 1;
      else if (value.toLowerCase() === "c") return 2;
      else if (value.toLowerCase() === "d") return 3;
      else if (value.toLowerCase() === "e") return 4;
      else return 5;
    },

    showShortAnswer() {
      if (Number(this.question?.is_correct) === 1)
        return this.question?.selected;
      else return this.question?.answer.split('"')[1];
    },
  },
};
</script>

<style lang="scss" scoped>
.exam-question-card {
  background: #ffffff;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 7px;

  .top-row {
    @include flex_row_between_nowrap;
    align-items: flex-start;
    padding-bottom: 4px;

    .left-row {
      @include flex_row_start_nowrap;
      align-items: flex-start;

      .counter {
        @include equal_min_edge(34);
        background: $brand_navy;
        border-radius: 10px;
        margin-right: 15px;
        position: relative;

        .count {
          @include transform-center;
          font-size: 13px;
          color: #fff;
        }
      }

      .question-info {
        color: $color_text;
        width: 100%;

        .question-text {
          font-size: 14.75px;
          line-height: 20px;
          margin-bottom: 4px;
        }

        .question-image {
          img {
            min-width: 25%;
            width: auto;
            max-width: 50%;
            height: auto;
            max-height: 260px;

            @include breakpoint_max(sm) {
              max-width: 60%;
            }

            @include breakpoint_max(xs) {
              max-height: 200px;
            }
          }
        }

        .question-detail {
          font-size: 12.75px;
          line-height: 16px;
        }
      }
    }

    .right-row {
      @include equal_min_edge(32);
      border-radius: 10px;
      border: 1px solid #e8e8e8;
      position: relative;

      .icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(0deg);
        font-size: 11px;
        transition: all 0.4s ease-in-out;
      }
    }

    .rotate-180 {
      transform: translate(-50%, -50%) rotate(180deg) !important;
    }
  }

  .bottom-row {
    @include flex_row_start_nowrap;
    padding-top: 11px;
    transition: all 0.4s ease-in-out;

    .spacer {
      @include equal_min_edge(34);
      background: $brand_navy;
      border-radius: 10px;
      margin-right: 15px;
      position: relative;
      visibility: hidden;
    }

    .option-block {
      width: 100%;
      padding-top: 10px;

      .top-option {
        position: relative;
        width: 100%;
        margin-bottom: 30px;
        border-top: 1px solid $border_grey;

        .text {
          position: absolute;
          color: $color_grey_dark;
          font-size: 13.5px;
          left: 0;
          top: -11px;
          z-index: 1;
          background: #fff;
          white-space: nowrap;
          padding-right: 5px;
        }
      }

      .mid-option {
        @include flex_row_start_wrap;
      }

      .bottom-option {
        @include flex_row_start_nowrap;

        .exam-btn {
          @include flex_row_center_nowrap;
          padding: 8px 18px;
          border-radius: 20px;
          font-size: 13.5px;
          width: max-content;
          cursor: pointer;
          transition: all 0.4s ease-in-out;

          .icon {
            transition: all 0.4s ease-in-out;
            margin-right: 12px;
            font-size: 14.5px;
          }

          .text {
            transition: all 0.4s ease-in-out;
            color: $color_grey_dark;
          }

          &:hover {
            box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
          }
        }

        .delete-btn {
          background: $brand_white;
          margin-right: 20px;

          .icon {
            color: $brand_tonic;
          }

          &:hover {
            background: $brand_red_light;
          }
        }
      }
    }
  }
}
</style>
